import React, { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { TrendingUp, ChevronLeft, ChevronRight, IndianRupee, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { IncomeTable, ExpensesTable, NetCashFlowTable } from './CashFlowTables';
import { AddExpenseDialog, EditExpenseDialog, DrillDownDialog } from './CashFlowDialogs';

export const CashFlowTab = ({ cashFlowData, getCashFlowMonths, cashflowPeriod, setCashflowPeriod, formatCurrency, onRefresh, onDeleteExpense, feeStructure, cashflowExpenses, reimbursements, projects }) => {
  const [isCashflowExpenseDialogOpen, setIsCashflowExpenseDialogOpen] = useState(false);
  const [drillDown, setDrillDown] = useState(null);
  const [cashflowExpenseForm, setCashflowExpenseForm] = useState({
    expense_head: '', sub_head: '', month_year: '', amount: '', is_recurring: false, recurring_months: 12
  });

  // Persist expand/collapse state across refreshes at parent level
  const [expandedHeads, setExpandedHeads] = useState({});
  const toggleExpand = (head) => setExpandedHeads(prev => ({ ...prev, [head]: !prev[head] }));

  // Edit dialog state
  const [isEditExpenseOpen, setIsEditExpenseOpen] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [editForm, setEditForm] = useState({ expense_head: '', sub_head: '', month_year: '', amount: '' });

  const handleEditExpense = (expenseId) => {
    const target = (cashflowExpenses || []).find(e => e.expense_id === expenseId);
    if (!target) {
      toast.error('Expense not found');
      return;
    }
    setEditingExpenseId(expenseId);
    setEditForm({
      expense_head: target.expense_head || '',
      sub_head: target.sub_head || '',
      month_year: target.month_year || '',
      amount: String(target.amount ?? '')
    });
    setIsEditExpenseOpen(true);
  };

  const handleEditExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!editingExpenseId) return;
    try {
      await axios.patch(`${API_URL}/cashflow-expenses/${editingExpenseId}`, {
        ...editForm, amount: parseFloat(editForm.amount)
      }, { withCredentials: true });
      toast.success('Expense updated');
      setIsEditExpenseOpen(false);
      setEditingExpenseId(null);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update expense');
    }
  };

  const handleCashflowExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        expense_head: cashflowExpenseForm.expense_head,
        sub_head: cashflowExpenseForm.sub_head,
        month_year: cashflowExpenseForm.month_year,
        amount: parseFloat(cashflowExpenseForm.amount),
        recurring_months: cashflowExpenseForm.is_recurring
          ? Math.max(2, Math.min(60, parseInt(cashflowExpenseForm.recurring_months, 10) || 12))
          : 1,
      };
      await axios.post(`${API_URL}/cashflow-expenses`, payload, { withCredentials: true });
      toast.success(payload.recurring_months > 1
        ? `Recurring expense added for ${payload.recurring_months} months`
        : 'Expense added to cash flow');
      setIsCashflowExpenseDialogOpen(false);
      setCashflowExpenseForm({ expense_head: '', sub_head: '', month_year: '', amount: '', is_recurring: false, recurring_months: 12 });
      onRefresh();
    } catch { toast.error('Failed to add expense'); }
  };

  // Derive a human-readable status for a fee item using both invoice & payment status
  const getFeeStatusLabel = (f) => {
    if (f.invoice_status === 'cancelled') return 'Cancelled';
    if (f.payment_status === 'void') return 'Voided';
    if (f.payment_status === 'received') return 'Received';
    if (f.payment_status === 'partial') return 'Partial';
    if (f.invoice_status === 'invoiced') return 'Invoiced';
    return 'Pending';
  };

  // Statuses that represent money that will NOT be received - excluded from totals
  const isExcludedFromTotal = (status) => status === 'Voided' || status === 'Cancelled';

  // Drill-down data helpers
  const getDrillDownDataForType = (type, monthKey) => {
    if (type === 'income') {
      const items = (feeStructure || [])
        .filter(f => f.tentative_billing_date && f.tentative_billing_date.slice(0, 7) === monthKey)
        .map(f => {
          const project = (projects || []).find(p => p.project_id === f.project_id);
          return {
            id: f.item_id,
            source: 'Fee Structure',
            project: project?.name || 'Unknown',
            description: `${f.stage || ''} ${f.deliverable || ''}`.trim(),
            date: f.tentative_billing_date,
            amount: f.amount,
            status: getFeeStatusLabel(f)
          };
        });
      const total = items.reduce((s, i) => s + (isExcludedFromTotal(i.status) ? 0 : i.amount), 0);
      return { items, total };
    }
    const items = [];
    (cashflowExpenses || []).filter(e => e.month_year === monthKey).forEach(e => {
      items.push({ id: e.expense_id, source: 'Cash Flow Expense', project: '-', description: `${e.expense_head}${e.sub_head ? ' - ' + e.sub_head : ''}`, date: monthKey, amount: e.amount, status: 'Recorded' });
    });
    (reimbursements || []).filter(r => r.date && r.date.slice(0, 7) === monthKey && r.status !== 'rejected').forEach(r => {
      items.push({ id: r.reimbursement_id, source: 'Reimbursement', project: r.project_id || '-', description: `${r.category}: ${r.description || ''}`, date: r.date, amount: r.amount, status: r.status });
    });
    return { items, total: items.reduce((s, i) => s + i.amount, 0) };
  };

  const getDrillDownData = () => {
    if (!drillDown) return { items: [], total: 0 };
    const { type, monthKey } = drillDown;
    if (type === 'income' || type === 'expense') {
      return getDrillDownDataForType(type, monthKey);
    }
    if (type === 'net') {
      const incomeData = getDrillDownDataForType('income', monthKey);
      const expenseData = getDrillDownDataForType('expense', monthKey);
      return {
        items: [
          ...incomeData.items.map(i => ({ ...i, category: 'Income' })),
          ...expenseData.items.map(i => ({ ...i, category: 'Expense', amount: -i.amount }))
        ],
        total: incomeData.total - expenseData.total
      };
    }
    return { items: [], total: 0 };
  };

  const drillDownData = getDrillDownData();
  const handleCellClick = (type, monthKey, monthName) => setDrillDown({ type, monthKey, monthName });

  return (
    <Card className="p-6" data-testid="cashflow-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
          <TrendingUp className="h-5 w-5" /> Cash Flow Projection
        </h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={cashflowPeriod === 0} onClick={() => setCashflowPeriod(0)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium px-2">
            {cashflowPeriod === 0 ? 'Months 1-6' : 'Months 7-12'}
          </span>
          <Button variant="outline" size="sm" disabled={cashflowPeriod === 1} onClick={() => setCashflowPeriod(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <IncomeTable
        months={cashFlowData.months}
        incomeByMonth={cashFlowData.incomeByMonth}
        formatCurrency={formatCurrency}
        onCellClick={handleCellClick}
      />

      {/* Expenses header with Add button */}
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-red-600 flex items-center gap-2">
          <IndianRupee className="h-4 w-4" /> Expenses (Editable)
        </h4>
        <Button size="sm" variant="outline" onClick={() => setIsCashflowExpenseDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />Add Expense
        </Button>
      </div>

      <ExpensesTable
        months={cashFlowData.months}
        expenseGroups={cashFlowData.expenseGroups}
        expensesByMonth={cashFlowData.expensesByMonth}
        formatCurrency={formatCurrency}
        onCellClick={handleCellClick}
        onEditExpense={handleEditExpense}
        onDeleteExpense={onDeleteExpense}
        expandedHeads={expandedHeads}
        onToggleExpand={toggleExpand}
      />

      <NetCashFlowTable
        months={cashFlowData.months}
        netByMonth={cashFlowData.netByMonth}
        formatCurrency={formatCurrency}
        onCellClick={handleCellClick}
      />

      {/* Dialogs */}
      <AddExpenseDialog
        open={isCashflowExpenseDialogOpen} onOpenChange={setIsCashflowExpenseDialogOpen}
        form={cashflowExpenseForm} setForm={setCashflowExpenseForm}
        onSubmit={handleCashflowExpenseSubmit} getCashFlowMonths={getCashFlowMonths}
      />
      <EditExpenseDialog
        open={isEditExpenseOpen} onOpenChange={setIsEditExpenseOpen}
        form={editForm} setForm={setEditForm}
        onSubmit={handleEditExpenseSubmit} getCashFlowMonths={getCashFlowMonths}
      />
      <DrillDownDialog
        drillDown={drillDown} setDrillDown={setDrillDown}
        drillDownData={drillDownData} formatCurrency={formatCurrency}
      />
    </Card>
  );
};
